import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Briefcase, ShieldCheck, Info } from "lucide-react";
import { AutosaveIndicator, Skeleton, SchemaFormRenderer } from "../../design-system";
import useAutosave from "../../hooks/useAutosave";
import { useProfile } from "../../context/ProfileContext";
import {
  fetchTiposUsuario,
  fetchSchema,
  fetchPerfilProfesional,
  updatePerfilProfesional,
  fetchRoles,
  addRol,
  setRolPrincipal,
} from "../../lib/profile";
import { humanizeAxiosError } from "../../lib/api";

/**
 * PerfilProfesionalPage — Identidad Profesional Única.
 *
 * El usuario tiene UN solo perfil profesional, derivado dinámicamente del
 * "Tipo de usuario" elegido en el Perfil Global. Sin multi-rol ni pestañas.
 * Los campos (Especialidad, Colegio Profesional, N° de Colegiatura, etc.)
 * provienen del schema del tipo y son la base del sistema de verificación.
 */

const TIPOS_SIN_PERFIL = new Set(["cliente", "admin"]);

export default function PerfilProfesionalPage() {
  const { perfilGlobal, refresh: refreshProfileCtx } = useProfile();
  const tipo = perfilGlobal?.tipo_usuario;

  const [tipos, setTipos] = useState([]);
  const [schema, setSchema] = useState(null);
  const [profesional, setProfesional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auto = useAutosave(async (patch) => {
    if (!tipo) return;
    try {
      const updated = await updatePerfilProfesional(tipo, { datos: patch });
      setProfesional(updated);
      setError(null);
      refreshProfileCtx?.();
    } catch (e) {
      setError(humanizeAxiosError(e));
      throw e;
    }
  });

  const cargar = useCallback(async () => {
    if (!tipo || TIPOS_SIN_PERFIL.has(tipo)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [t, s, roles] = await Promise.all([
        fetchTiposUsuario(),
        fetchSchema(tipo),
        fetchRoles(),
      ]);
      setTipos(t);
      setSchema(s);
      // Garantizar que el rol único (= tipo global) exista y sea principal.
      const existente = (roles.roles || []).find((r) => r.tipo === tipo);
      if (!existente) {
        await addRol(tipo);
        await setRolPrincipal(tipo);
      } else if (!existente.es_principal) {
        await setRolPrincipal(tipo);
      }
      const p = await fetchPerfilProfesional(tipo);
      setProfesional(p);
      refreshProfileCtx?.();
    } catch (e) {
      setError(humanizeAxiosError(e));
    } finally {
      setLoading(false);
    }
  }, [tipo, refreshProfileCtx]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const tipoMeta = useMemo(
    () => tipos.find((t) => t.codigo === tipo),
    [tipos, tipo]
  );

  if (!tipo || TIPOS_SIN_PERFIL.has(tipo)) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center" data-testid="pp-no-aplica">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center">
          <Briefcase size={24} />
        </div>
        <h3 className="mt-4 text-lg font-bold text-slate-900">El perfil profesional no aplica a tu cuenta</h3>
        <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
          Cambia tu <strong>Tipo de usuario</strong> en el Perfil Global a Maestro, Profesional,
          Contratista o Ferretería para activar tu identidad profesional.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4" data-testid="pp-loading">
        <Skeleton variant="title" />
        <Skeleton variant="input" count={4} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="perfil-profesional-page">
      <div className="bg-white rounded-2xl border border-slate-100">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand-blue/10 text-brand-navy flex items-center justify-center flex-shrink-0">
              <Briefcase size={22} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900" data-testid="pp-tipo-titulo">
                {tipoMeta?.nombre_visible || "Perfil Profesional"}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5 max-w-md">
                Tu identidad profesional única. Completa tus datos: son la base de tu reputación y verificación.
              </p>
            </div>
          </div>
          <AutosaveIndicator state={auto.state} lastSavedAt={auto.lastSavedAt} testid="pp-autosave" />
        </div>

        {/* Contenido */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm" role="alert" data-testid="pp-error">
              {error}
            </div>
          )}

          {profesional && (
            <div className="flex items-center gap-2 text-xs" data-testid="pp-operativo-badge">
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border font-semibold ${profesional.perfil_operativo ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
                {profesional.perfil_operativo ? (
                  <><ShieldCheck size={13} /> Perfil operativo</>
                ) : (
                  <><Info size={13} /> Completa los campos obligatorios (*)</>
                )}
              </span>
            </div>
          )}

          {schema && profesional && (
            <SchemaFormRenderer
              schema={schema}
              values={profesional.datos}
              onFieldBlur={(nombre, valor) => auto.schedule({ [nombre]: valor })}
              testidPrefix="pp-form"
            />
          )}
        </div>
      </div>
    </div>
  );
}
